import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import DataTable from 'components/ui/data-table';
import LoadingState from '../../shared/LoadingState';

const TIER_META = {
  dormant: { label: 'Dormant (45d+)', variant: 'destructive' },
  cooling: { label: 'Cooling (14–45d)', variant: 'warning' },
  untracked: { label: 'Untracked', variant: 'outline' },
  engaged: { label: 'Engaged (<14d)', variant: 'success' },
};

const CLASS_LABELS = {
  active_cancel: 'Active cancel (used app that month)',
  dormant_cancel: 'Dormant cancel (gone 30d+ before)',
  never_tracked: 'Never tracked (no GA footprint)',
};

export default function RetentionTab({ dormant, cancelFunnel, churnContext, onOpenJourney, loading }) {
  const [tierFilter, setTierFilter] = useState('risk');

  const watchlistRows = useMemo(() => {
    const rows = dormant?.data || [];
    if (tierFilter === 'risk') {
      return rows.filter((r) => r.tier === 'dormant' || r.tier === 'cooling');
    }
    if (tierFilter === 'all') return rows;
    return rows.filter((r) => r.tier === tierFilter);
  }, [dormant, tierFilter]);

  const copyEmails = () => {
    const emails = watchlistRows.map((r) => r.email).filter(Boolean);
    navigator.clipboard.writeText(emails.join(', '));
    toast.success(`Copied ${emails.length} emails`);
  };

  const watchlistColumns = useMemo(() => [
    {
      accessorKey: 'tier',
      header: 'Tier',
      cell: ({ row }) => {
        const t = TIER_META[row.original.tier] || {};
        return <Badge variant={t.variant}>{t.label || row.original.tier}</Badge>;
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      meta: { cellClassName: 'text-token-sm' },
      cell: ({ row }) => (
        <button
          type="button"
          className="cursor-pointer text-left text-primary hover:underline"
          onClick={() => onOpenJourney?.(row.original.wp_id)}
          title="Open user journey"
        >
          {row.original.email || row.original.wp_id}
        </button>
      ),
    },
    { accessorKey: 'subscription_type', header: 'Plan', meta: { cellClassName: 'text-token-sm' } },
    {
      accessorKey: 'amount_gbp',
      header: '£',
      meta: { numeric: true },
      cell: ({ row }) => (row.original.amount_gbp != null ? `£${row.original.amount_gbp.toFixed(2)}` : '—'),
    },
    {
      accessorKey: 'next_billing_date',
      header: 'Next renewal',
      cell: ({ row }) => row.original.next_billing_date || '—',
    },
    {
      accessorKey: 'last_active',
      header: 'Last active',
      cell: ({ row }) => {
        const r = row.original;
        if (!r.last_active) return <span className="text-muted-foreground">never tracked</span>;
        return (
          <span className={cn(r.days_since_active > 45 && 'font-semibold text-error')}>
            {r.last_active} ({r.days_since_active}d)
          </span>
        );
      },
    },
    { accessorKey: 'sessions_30d', header: 'Sessions 30d', meta: { numeric: true } },
  ], [onOpenJourney]);

  // Cancel-flow funnel display order
  const FUNNEL_ORDER = [
    'cancel_flow_started', 'cancel_aborted', 'cancel_switched_to_pause',
    'cancel_switched_to_upgrade', 'cancel_confirmed',
  ];
  const funnelSteps = useMemo(() => {
    const byEvent = {};
    (cancelFunnel?.funnel || []).forEach((f) => { byEvent[f.event] = f; });
    return FUNNEL_ORDER.map((ev) => ({
      event: ev.replace('cancel_', '').replace(/_/g, ' '),
      users: byEvent[ev]?.users || 0,
      events: byEvent[ev]?.events || 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelFunnel]);

  const monthlyData = useMemo(() => {
    const months = churnContext?.monthly || [];
    return months.slice(-18);
  }, [churnContext]);

  if (!dormant && loading !== false) {
    return <LoadingState message="Loading retention data..." />;
  }

  const tiers = dormant?.tiers || {};

  return (
    <div className="w-full min-w-0">
      <p className="mb-3 text-token-xs text-muted-foreground">
        Subscription data is global — the property / gameweek filters above don&apos;t apply to
        this tab (dates apply to the cancel flow and churn context only).
      </p>
      {/* Dormant subscriber watchlist */}
      <div className="mb-4">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h3 className="text-token-xl font-semibold">Subscriber Watchlist</h3>
          <ToggleGroup type="single" size="sm" value={tierFilter} onValueChange={(v) => v && setTierFilter(v)}>
            <ToggleGroupItem value="risk">At risk ({(tiers.dormant || 0) + (tiers.cooling || 0)})</ToggleGroupItem>
            <ToggleGroupItem value="dormant">Dormant ({tiers.dormant || 0})</ToggleGroupItem>
            <ToggleGroupItem value="cooling">Cooling ({tiers.cooling || 0})</ToggleGroupItem>
            <ToggleGroupItem value="untracked">Untracked ({tiers.untracked || 0})</ToggleGroupItem>
            <ToggleGroupItem value="all">All ({dormant?.total_subscribers || 0})</ToggleGroupItem>
          </ToggleGroup>
          <Button variant="outline" size="sm" className="ml-auto" onClick={copyEmails}>
            <Copy /> Copy emails
          </Button>
        </div>
        <p className="mb-2 text-token-xs text-muted-foreground">
          Paying subscribers ranked by risk, then next renewal — the rescue-email list.
          Untracked = never seen in GA (ad-blockers), not necessarily inactive.
        </p>
        <DataTable
          columns={watchlistColumns}
          data={watchlistRows}
          maxHeight={420}
          emptyMessage="Nobody in this tier."
        />
      </div>

      {/* Cancel-flow funnel */}
      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-token-large border border-borderGrey bg-white p-4">
          <h3 className="mb-2 text-token-xl font-semibold">Cancel Flow (server-side)</h3>
          {cancelFunnel?.has_data ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={funnelSteps}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
                  <XAxis dataKey="event" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="users" fill="#254A73" name="Users" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-2 text-token-sm">
                {Object.entries(cancelFunnel.survival || {}).map(([ev, s]) => (
                  s.saved > 0 && (
                    <p key={ev} className="text-muted-foreground">
                      {ev.replace('cancel_', '').replace(/_/g, ' ')}: {s.saved} saved,{' '}
                      <span className={cn('font-semibold', s.still_subscribed / s.saved >= 0.5 ? 'text-success' : 'text-error')}>
                        {s.still_subscribed} still subscribed
                      </span>
                    </p>
                  )
                ))}
                {cancelFunnel.abandoned_users > 0 && (
                  <p className="text-muted-foreground">
                    {cancelFunnel.abandoned_users} started the flow with no recorded outcome.
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-token-sm text-muted-foreground">
              No events logged yet. The cancel-flow funnel is built from the first-party
              subscription_events log (the old GA4 path never recorded these).
            </p>
          )}
        </div>

        {/* Cancel reasons */}
        <div className="rounded-token-large border border-borderGrey bg-white p-4">
          <h3 className="mb-2 text-token-xl font-semibold">Cancellation Reasons</h3>
          {(churnContext?.reasons || []).length > 0 ? (
            <table className="w-full border-separate border-spacing-0 text-sm">
              <tbody>
                {churnContext.reasons.slice(0, 10).map((r) => (
                  <tr key={r.reason_code}>
                    <td className="border-b border-borderGrey p-1.5 text-muted-foreground">{r.reason_code}</td>
                    <td className="border-b border-borderGrey p-1.5 text-right font-semibold">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-token-sm text-muted-foreground">No exit-survey responses yet.</p>
          )}
        </div>
      </div>

      {/* Churn classification */}
      <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-1 text-token-xl font-semibold">What Kind of Cancels?</h3>
        <p className="mb-2 text-token-xs text-muted-foreground">
          Churn here is event-driven (season end, renewal emails), not gradual decay — classify each
          cancel instead of averaging behaviour. Dormant cancels need earlier rescue (watchlist);
          active cancels are product/price signals worth reading individually.
        </p>
        <div className="mb-3 flex flex-wrap gap-4">
          {Object.entries(churnContext?.classes || {}).map(([cls, n]) => (
            <div key={cls} className="rounded-token-md border border-borderGrey px-3 py-2">
              <p className="text-token-xs text-muted-foreground">{CLASS_LABELS[cls] || cls}</p>
              <p className="text-token-xl font-semibold">{n}</p>
            </div>
          ))}
        </div>
        {monthlyData.length > 0 && (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthlyData}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="active_cancel" stackId="a" fill="#f44336" name="Active cancel" />
              <Bar dataKey="dormant_cancel" stackId="a" fill="#ff9800" name="Dormant cancel" />
              <Bar dataKey="never_tracked" stackId="a" fill="#9e9e9e" name="Never tracked" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
